import { HeroIcons } from '../lib/utils/heroIcons'
import { useTranslation } from 'next-i18next'
import { NextSeo } from 'next-seo'
import Link from '../components/shared/Link'
import routes from '../config/route'
import { makeStaticProps } from '../lib/getStatic'
import BackgroundPicture from '../components/shared/BackgroundPicture'

const lightBackgroundSources = [
  {
    media: '(min-width: 768px)',
    srcSet: '/img/backgrounds/bg-light-desktop.webp',
    type: 'image/webp',
  },
  {
    media: '(min-width: 768px)',
    srcSet: '/img/backgrounds/bg-light-desktop.png',
    type: 'image/png',
  },
  {
    srcSet: '/img/backgrounds/bg-light-mobile.webp',
    type: 'image/webp',
  },
]

const teamMembers = [
  {
    name: 'Adel Louarrani',
    image: '/img/faces/adel-louarrani.png',
    imageWebp: '/img/faces/adel-louarrani.webp',
  },
  {
    name: 'Adrien Vandenbossche',
    image: '/img/faces/adrien-vandenbossche.png',
    imageWebp: '/img/faces/adrien-vandenbossche.webp',
  },
  {
    name: 'Matthieu Courtiaud',
    image: '/img/faces/matthieu-courtiaud.png',
    imageWebp: '/img/faces/matthieu-courtiaud.webp',
  },
  {
    name: 'Loïc Debal',
    image: '/img/faces/loic-debal.png',
    imageWebp: '/img/faces/loic-debal.webp',
  },
]

export default function TeamPage() {
  const { t } = useTranslation('team')

  return (
    <>
      <NextSeo
        title={t('seo.title') || ''}
        description={t('seo.description') || ''}
      />

      <main className="overflow-hidden bg-[#f6f7ff]">
        <section className="bg-gradient-box overflow-hidden">
          <div className="relative mx-auto grid w-full gap-12 px-3 pb-20 pt-28 tablet:px-16 desktop:grid-cols-[1.05fr_0.95fr] desktop:items-center desktop:gap-16 desktop:px-24 desktop:pb-24 desktop:pt-28 2xl:px-44">
            <div className="pointer-events-none absolute -left-32 top-32 h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.16)_0%,transparent_70%)]" />
            <div className="pointer-events-none absolute -right-24 top-8 h-[480px] w-[480px] rounded-full bg-[radial-gradient(circle,rgba(188,174,255,0.24)_0%,transparent_72%)]" />

            <div className="relative z-10">
              <h1 className="max-w-3xl px-3 font-semibold text-white text-shelters-display-title tablet:px-0 desktop:max-w-none desktop:text-[3.75rem]">
                <span className="desktop:whitespace-nowrap">
                  {t('hero.title.line1')}
                </span>
                <br />
                <span className="text-white/65 desktop:whitespace-nowrap">
                  {t('hero.title.highlight')}
                </span>
              </h1>
              <p className="mt-6 max-w-2xl px-3 text-white/75 text-shelters-section-body tablet:px-0">
                {t('hero.description')}
              </p>

              <div className="mt-8 flex flex-col gap-3 px-3 sm:flex-row tablet:px-0">
                <Link
                  href={routes.contact}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-button border border-white/60 bg-white/95 px-5 text-sm font-semibold text-[#293254] shadow-[0_12px_28px_rgba(18,15,62,0.2)] transition hover:-translate-y-0.5 hover:bg-white"
                >
                  {t('hero.primaryCta')}
                  <HeroIcons.ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="relative z-10 mx-auto w-full max-w-[560px] px-3 tablet:px-0 desktop:mx-0 desktop:justify-self-end">
              <div className="pointer-events-none absolute inset-8 rounded-container border border-white/20 bg-white/10 shadow-[0_28px_70px_rgba(18,15,62,0.3)] backdrop-blur-xl" />

              <div className="relative grid grid-cols-2 gap-3 tablet:gap-4">
                {teamMembers.map((member, index) => (
                  <article
                    key={member.name}
                    className={`group relative overflow-hidden rounded-card border border-white/30 bg-white/10 p-1.5 shadow-[0_20px_48px_rgba(18,15,62,0.28)] backdrop-blur-xl tablet:p-2 ${
                      index % 2 === 1 ? 'translate-y-5' : ''
                    }`}
                  >
                    <div className="relative aspect-square overflow-hidden rounded-inner-card">
                      <picture>
                        <source srcSet={member.imageWebp} type="image/webp" />
                        <img
                          src={member.image}
                          alt={member.name}
                          className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                        />
                      </picture>
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_55%,rgba(18,15,62,0.72)_100%)]" />
                      <p className="absolute bottom-3 left-3 right-3 text-sm font-semibold text-white tablet:bottom-4 tablet:left-4 tablet:right-4 tablet:text-base">
                        {member.name}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="relative overflow-hidden">
          <BackgroundPicture
            fallbackSrc="/img/backgrounds/bg-light-mobile.png"
            objectPositionClassName="object-top"
            sources={lightBackgroundSources}
          />
          <section className="relative z-10 px-3 py-20 tablet:px-16 tablet:py-28 desktop:px-24 2xl:px-44">
            <div className="grid items-start gap-10 desktop:grid-cols-[0.8fr_1.2fr] desktop:gap-24">
              <div className="px-3">
                <p className="text-shelters-kicker font-semibold uppercase tracking-[0.18em] text-sheltersBlue">
                  {t('mission.eyebrow')}
                </p>
                <h2 className="mt-4 max-w-2xl font-semibold text-[#151a3c] text-shelters-section-title">
                  {t('mission.title')}
                </h2>
              </div>
              <div className="border-l-2 border-[#d9def8] pl-6 tablet:pl-8">
                <p className="max-w-3xl text-[#515b7a] text-shelters-section-body">
                  {t('mission.description')}
                </p>
                <p className="mt-6 max-w-3xl text-lg font-medium leading-relaxed text-[#293254] tablet:text-xl">
                  {t('mission.statement')}
                </p>
              </div>
            </div>
          </section>

          <section className="relative z-10 px-3 pb-24 tablet:px-16 tablet:pb-32 desktop:px-24 2xl:px-44">
            <div className="bg-gradient-box relative overflow-hidden rounded-container border border-white/35 p-7 shadow-[0_24px_60px_rgba(18,15,62,0.24)] tablet:p-10 desktop:flex desktop:items-end desktop:justify-between desktop:gap-12 desktop:p-12">
              <div className="pointer-events-none absolute -right-20 -top-28 h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.18)_0%,transparent_70%)]" />
              <div className="relative z-10 desktop:max-w-none">
                <p className="text-shelters-kicker font-semibold uppercase tracking-[0.18em] text-white/65">
                  {t('cta.eyebrow')}
                </p>
                <h2 className="mt-4 font-semibold text-white text-shelters-section-title desktop:whitespace-nowrap">
                  {t('cta.title')}
                </h2>
              </div>

              <div className="relative z-10 mt-8 shrink-0 desktop:mt-0">
                <Link
                  href={routes.contact}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-button border border-white/60 bg-white/95 px-5 text-sm font-semibold text-[#293254] transition hover:-translate-y-0.5 hover:bg-white"
                >
                  {t('cta.primary')}
                  <HeroIcons.ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  )
}

const getStaticProps = makeStaticProps([
  'common',
  'team',
  'index',
  'navbar',
  'footer',
  'modals',
  'newsletter',
])

export { getStaticProps, TeamPage }
